// =============================================================================
// VandieZ agent-web -- My Bids (/bids)
// =============================================================================
// Lists all bids placed for the currently-selected owner, sorted by
// created_at DESC. Highlights bids placed by an agent with a "by agent" badge.
//
// v1: read-only. Withdraw bid action is deferred to Phase 2.
// =============================================================================

import { Loader2, Gavel } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { bidsApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type { Bid, BidStatus } from "@/types/bid";

const STATUS_LABEL: Record<BidStatus, string> = {
  active: "Active",
  accepted: "Accepted",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  expired: "Expired",
  superseded: "Superseded",
};

const STATUS_TONE: Record<BidStatus, string> = {
  active: "bg-success-subtle text-success-subtle-foreground",
  accepted: "bg-primary-subtle text-primary-subtle-foreground",
  rejected: "bg-danger-subtle text-danger-subtle-foreground",
  withdrawn: "bg-muted text-muted-foreground",
  expired: "bg-muted text-muted-foreground",
  superseded: "bg-warning-subtle text-warning-subtle-foreground",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function BidsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const { currentOwnerId, currentOwner } = useOwnerContext();
  const [bids, setBids] = useState<Bid[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    bidsApi
      .myBids(accessToken, currentOwnerId, { limit: 50 }, controller.signal)
      .then((res) => {
        // Sort by created_at DESC
        const sorted = [...res.items].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        setBids(sorted);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError) {
          const code = err.problem.code;
          if (err.problem.status === 403 && code === "AGENT_PERMISSION_MISSING") {
            setError("Ask the owner to grant the 'bids:read' permission on 'bids'.");
          } else if (err.problem.status === 403 && code === "AGENT_NO_GRANT_FOR_OWNER") {
            setError("Your access to this owner was revoked. Switch owner via the header dropdown.");
          } else if (err.problem.status === 422 && code === "AGENT_OWNER_PARAM_REQUIRED") {
            setError("No owner selected — switch via the header dropdown.");
          } else {
            setError(err.problem.detail);
          }
        } else {
          setError("Couldn't load bids.");
        }
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId]);

  const ownerName = currentOwner?.owner_full_name ?? "This owner";

  if (currentOwnerId === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="My bids" />
        <main className="mx-auto max-w-4xl px-6 py-16 text-center text-muted-foreground">
          No owner selected — head to{" "}
          <Link to="/" className="font-medium text-primary underline underline-offset-2">
            My Owners
          </Link>{" "}
          and pick one.
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="My bids" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : bids === null || bids.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Gavel className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No bids yet</CardTitle>
              <CardDescription>
                {ownerName} hasn't placed any bids yet. Open the{" "}
                <Link
                  to={`/marketplace?as=${currentOwnerId}`}
                  className="font-medium text-primary underline underline-offset-2"
                >
                  Marketplace
                </Link>{" "}
                to find loads.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {bids.map((bid) => (
              <Card key={bid.id}>
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[bid.status]}`}
                        >
                          {STATUS_LABEL[bid.status]}
                        </span>
                        {bid.placed_by_agent ? (
                          <span className="inline-flex items-center rounded-full bg-info-subtle px-2 py-0.5 text-xs font-medium text-info-subtle-foreground">
                            by agent
                          </span>
                        ) : null}
                        <span className="text-xs text-muted-foreground">
                          {bid.bidder_role} · rank #{bid.bidder_rank_at_submission}
                        </span>
                      </div>
                      <p className="text-sm font-medium">
                        {formatINR(bid.bid_amount)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Load:{" "}
                        <Link
                          to={`/marketplace/${bid.load_id}?as=${currentOwnerId}`}
                          className="font-mono text-primary hover:underline"
                          title={bid.load_id}
                        >
                          {truncateId(bid.load_id)}
                        </Link>
                      </p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground space-y-1">
                      <p>Placed: {formatDateTime(bid.created_at)}</p>
                      <p>Expires: {formatDateTime(bid.bid_expires_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
